import React, { useState } from "react";
import "./SharedStyle.css";
import { Region, StoreGroup } from "../../Modules/Companies/CompanyDataTypes";
import { Edit, Trash, Save } from "@rsuite/icons";
import { Button, CheckPicker, Col, IconButton, Row } from "rsuite";
import MinusRoundIcon from "@rsuite/icons/MinusRound";
import PlusRoundIcon from "@rsuite/icons/PlusRound";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../Modals/AlertModal/AlertModal";
import { isEmptyVal, isNotEmpty } from "../../../utils/helpers";
import {
  defaultStoreGroupFor,
  groupForObjArr,
} from "../../../core/config/AppConstants";
import StoreGroupItem from "./StoreGroupItem";

interface RegionProps {
  region: Region;
  onDeleteRegion?: Function;
  onUpdateRegion?: Function;
  onDeleteStoreGroup?: Function;
  onUpdateStoreGroup?: Function;
  level: number;
  onAddStoreGroup?: Function;
  allowLevel: number;
}

const RegionTree: React.FC<RegionProps> = ({
  region,
  onAddStoreGroup,
  onDeleteRegion,
  onUpdateRegion,
  onDeleteStoreGroup,
  onUpdateStoreGroup,
  level,
  allowLevel,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [regionValue, setRegionValue] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [loader, setLoading] = useState(false);
  const [newStoreGroupName, setNewStoreGroupName] = useState("");
  const [newStoreGroupFor, setNewStoreGroupFor] =
    useState(defaultStoreGroupFor);
  if (isEmptyVal(region?.store_groups)) region.store_groups = [];
  const addStoreGroup = () => {
    let storeGroup: StoreGroup = {};
    if (!newStoreGroupName.trim()) {
      UtilityServices.showToaster({
        type: "error",
        message: t("REQUIRED", {
          field: t("GROUP").toLowerCase() + " " + t("NAME").toLowerCase(),
        }),
      });
      return;
    }
    if (newStoreGroupFor.length < 1) {
      UtilityServices.showToaster({
        type: "error",
        message: t("PLEASE_SELECT_FIELD", {
          field: t("GROUP").toLowerCase() + " " + t("FOR").toLowerCase(),
        }),
      });
      return;
    }
    storeGroup.group_name = newStoreGroupName.trim();
    storeGroup.group_for = newStoreGroupFor.join();
    storeGroup.region_id = region.id;
    onAddStoreGroup && onAddStoreGroup(storeGroup);
    resetAddForm();
  };

  const resetAddForm = () => {
    setNewStoreGroupName("");
    setNewStoreGroupFor(defaultStoreGroupFor);
    setShowAddForm(false);
  };

  const handleDelete = (region: any) => {
    onDeleteRegion && onDeleteRegion(region);
  };

  const handleUpdate = (regionId: any, region: any) => {
    if (!regionValue.trim()) {
      UtilityServices.showToaster({
        type: "error",
        message: t("REQUIRED", {
          field: t("REGION").toLowerCase() + " " + t("NAME").toLowerCase(),
        }),
      });
      return;
    }
    region.region_name = regionValue.trim();
    onUpdateRegion && onUpdateRegion(regionId, region);
    setIsEdit(false);
  };

  return (
    <div className="pl-4">
      <div className="flex items-center cursor-pointer py-1 hover:text-blue-600">
        {allowLevel > level && (
          <Button
            appearance="link"
            onClick={() => setIsOpen(!isOpen)}
            className="me-2"
            title={t("STORE") + " " + t("GROUPS")}
          >
            {"  "}
            {region.store_groups?.length ?? 0}
            {"  "}
            {isOpen ? <MinusRoundIcon /> : <PlusRoundIcon />}
          </Button>
        )}
        <span>
          {isEdit ? (
            <input
              type="text"
              value={regionValue}
              onChange={(e) => {
                setRegionValue(e.target.value);
              }}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder={t("REGION") + " " + t("NAME")}
            />
          ) : (
            region.region_name
          )}
          <span className="ms-8 flex ">
            {isEdit ? (
              <>
                <IconButton
                  appearance="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdate(region.id, region);
                  }}
                  title={t("SAVE")}
                  icon={<Save />}
                />
              </>
            ) : (
              <>
                <IconButton
                  appearance="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRegionValue(region.region_name);
                    setIsEdit(true);
                  }}
                  title={t("UPDATE")}
                  icon={<Edit />}
                />
              </>
            )}
            <>
              <IconButton
                appearance="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDeleteModal(true);
                }}
                title={t("DELETE")}
                icon={<Trash />}
              />
            </>
          </span>
        </span>
      </div>

      {/* Store Group */}
      {isOpen && (
        <div className="ml-4 border-l border-gray-300">
          <Row
            className="header-row"
            style={{
              fontWeight: "bold",
              background: "#f5f5f5",
              padding: "10px",
            }}
          >
            <Col xl={6}>
              {t("GROUP")} {t("NAME")}
            </Col>
            <Col xl={6}>{t("FOR")}</Col>
            <Col xl={6}>
              {t("STORE")} {t("COUNT")}
            </Col>
            <Col xl={6}>{t("ACTION")}</Col>
          </Row>
          {region.store_groups.map((group) => (
            <StoreGroupItem
              key={group.id}
              storeGroup={group}
              onDeleteStoreGroup={onDeleteStoreGroup}
              onUpdateStoreGroup={onUpdateStoreGroup}
            />
          ))}
          {region.store_groups.length < 1 && (
            <Row>
              <Col>
                {t("NO_DATA_FOUND_OF", {
                  field:
                    t("STORE").toLowerCase() + " " + t("GROUP").toLowerCase(),
                })}
              </Col>
            </Row>
          )}
          {showAddForm && (
            <div className="mt-2 flex items-center gap-2">
              <Row>
                <Col>
                  <input
                    type="text"
                    value={newStoreGroupName}
                    onChange={(e) => setNewStoreGroupName(e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                    placeholder={
                      t("ENTER") + " " + t("GROUP") + " " + t("NAME")
                    }
                  />
                </Col>
                <Col>
                  {" "}
                  <label>{t("FOR")}</label>
                </Col>
                <Col>
                  <CheckPicker
                    sticky
                    onChange={(e: any) => {
                      let grpFor = e.filter(isNotEmpty);
                      setNewStoreGroupFor(grpFor);
                    }}
                    onClean={() => {
                      setNewStoreGroupFor([]);
                    }}
                    data={groupForObjArr}
                    value={newStoreGroupFor}
                    placeholder={t("GROUP") + " " + t("FOR")}
                  />
                </Col>
                <Col>
                  <Button
                    appearance="primary"
                    loading={loader}
                    onClick={addStoreGroup}
                  >
                    {t("ADD")}
                  </Button>
                </Col>
                <Col>
                  <Button
                    appearance="default"
                    onClick={() => setShowAddForm(false)}
                  >
                    {t("CANCEL")}
                  </Button>
                </Col>
              </Row>
            </div>
          )}

          {!showAddForm && allowLevel > level && (
            <Button appearance="primary" onClick={() => setShowAddForm(true)}>
              {t("ADD")} {t("STORE")} {t("GROUP")}
            </Button>
          )}
        </div>
      )}

      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            handleDelete(region);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("REGION").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
};

export default RegionTree;
